import { Injectable, Injector, inject } from '@angular/core';
import moment, { Moment } from 'moment';
import { Observable, from as from$, of, throwError } from 'rxjs';
import {
    catchError,
    map,
    mergeMap,
    reduce,
    shareReplay,
    toArray,
} from 'rxjs/operators';
import {
    compact as _compact,
    get as _get,
    toString as _toString,
    uniq as _uniq,
} from 'lodash';
import {
    LazadaStoreConfig,
    ShopeeStoreConfig,
    StoreConfig,
    StoreId,
    STORES,
    TiktokStoreConfig,
} from '../../config/store-config';
import {
    SHOPEE_LIST_WINDOW_DAYS,
    splitWindows,
    storeService,
} from '../common/store-scoped';
import { STORE_SYNC_CONCURRENCY } from '../common/throttle-retry';
import { LazadaOpenService } from '../lazada/lazada-open.service';
import { OrderStatus as LazadaOrderStatus } from '../lazada/enums/order-status';
import { ShopeeOpenServiceV2 } from '../shopee/shopee-open-v2.service';
import { OrderStatus as ShopeeOrderStatus } from '../shopee/enums/order-status-v2';
import { TiktokOpenService } from '../tiktok/tiktok-open.service';
import { OrderStatus as TiktokOrderStatus } from '../tiktok/enums/order-status';
import { UtilService } from '../util.service';
import {
    codeMatchesAny,
    isShopeeFailedDelivery,
    isTiktokFailedDelivery,
    normalizeCode,
} from './return-package';

// Finds a cancelled order by a scanned code, for the returns page's scan
// box: a parcel can be packed and labelled, then the order cancelled before
// the courier collects it, and that parcel comes back to the counter looking
// exactly like a return. It is not one — a cancellation restocks on its own
// — so the page must recognise it and record nothing.
//
// An order number goes straight to the platform's order detail. A tracking
// number cannot — the platforms have no "order by tracking number" call —
// so an index of recent cancelled orders is built on first use, keyed by
// both order number and tracking number, and kept for a while.
//
// A failed delivery is NOT one of these, although Shopee and TikTok mark it
// cancelled: the courier had it and brings it back, and the returns page
// lists it as "ตีกลับ". It is kept out of the index, and a direct lookup
// reports it as an order with `failedDelivery` set.

export interface CancelledOrder {
    storeId: StoreId;
    storeTitle: string;
    orderNo: string;
    trackingNumbers: string[];
    createdAt: Moment | null;
}

export interface CancelledOrderLookup {
    match: CancelledOrder | null;
    // Set when the code was an order number that the platform knows but the
    // order is NOT cancelled — the status says what it is instead, so the
    // operator can tell a live order from a genuine miss. `failedDelivery`:
    // the order is a failed delivery on its way or back to the shop.
    otherStatus?: {
        storeTitle: string;
        orderNo: string;
        status: string;
        failedDelivery: boolean;
    };
    // Stores that could not be asked: a miss is not a verdict while this is
    // non-empty.
    failed: string[];
}

// Which platform an order number belongs to, from its shape alone:
//   Shopee  260922PFMNQ966       date prefix + 8 alphanumerics, 14 in all
//   Lazada  1120008489658226     15–16 digits
//   TikTok  586200350782949218   18 digits
// A tracking number matches none of these (TH0105964ENQ0B, JTTH2015…,
// LEXCRPU…), which is what routes a scanned label to the index instead.
export type OrderNumberPlatform = 'shopee' | 'lazada' | 'tiktok';

export const orderNumberPlatform = (
    code: string,
): OrderNumberPlatform | null => {
    const key = normalizeCode(code);
    if (/^\d{6}[A-Z0-9]{8}$/.test(key)) {
        return 'shopee';
    }
    if (/^\d{18}$/.test(key)) {
        return 'tiktok';
    }
    if (/^\d{15,16}$/.test(key)) {
        return 'lazada';
    }
    return null;
};

// Whether a scanned code reads like a courier's tracking label — letters,
// then a long run of digits (TH0105964ENQ0B, SPXTH060540617169,
// LEXPU0712675712, JTTH201590412866) — rather than an order or return
// number (digits first) or a SKU (a short number). A courier sends a parcel
// it cannot deliver back under a return label of its own, which no
// marketplace records; only the original label identifies the order.
export const looksLikeCourierLabel = (code: string): boolean =>
    /^[A-Z]{2,}[A-Z0-9]*\d{6,}/.test(normalizeCode(code));

export const matchesCancelledOrder = (
    order: CancelledOrder,
    code: unknown,
): boolean =>
    codeMatchesAny(
        [order.orderNo, ...order.trackingNumbers].map(normalizeCode),
        code,
    );

// An order as a direct detail call describes it.
interface DirectOrder {
    storeTitle: string;
    storeId: StoreId;
    orderNo: string;
    status: string;
    // Cancelled before the courier had it — never true of a failed
    // delivery, whatever its status reads.
    cancelled: boolean;
    failedDelivery: boolean;
    trackingNumbers: string[];
    createdAt: Moment | null;
}

interface StoreIndex {
    orders: CancelledOrder[];
    failedTitle?: string;
}

// A label printed more than this long ago and still on the counter is not
// the case this exists for.
const LOOKBACK_DAYS = 45;
const INDEX_TTL_MS = 10 * 60 * 1000;

// Shopee hands out tracking numbers one call per order, so only orders whose
// shipment was actually arranged are asked — an order cancelled before any
// label existed has nothing to scan.
const SHOPEE_TRACKING_CONCURRENCY = 2;

// Shopee's answer for an order_sn that belongs to another shop (verified
// 2026-09-22): an error, not an empty list. It means "not this store", not
// that the store could not be asked.
const SHOPEE_NOT_FOUND = 'error_not_found';

@Injectable({
    providedIn: 'root',
})
export class CancelledOrderLookupService {
    private injector = inject(Injector);
    private utilService = inject(UtilService);
    private index: { at: number; result: Observable<StoreIndex[]> } | null =
        null;

    // An order number goes straight to the platform's order detail — one
    // call per store of that platform, instant, and it also says what a
    // non-cancelled order is. Anything else (a scanned tracking number) can
    // only be answered from the index of recent cancelled orders.
    find(code: string): Observable<CancelledOrderLookup> {
        const platform = orderNumberPlatform(code);
        if (!platform) {
            return this.findInIndex(code);
        }
        return this.findDirect(platform, code).pipe(
            mergeMap(({ order, failed }) => {
                if (order?.cancelled) {
                    return of({
                        match: {
                            storeId: order.storeId,
                            storeTitle: order.storeTitle,
                            orderNo: order.orderNo,
                            trackingNumbers: order.trackingNumbers,
                            createdAt: order.createdAt,
                        },
                        failed,
                    });
                }
                if (order) {
                    return of({
                        match: null,
                        otherStatus: {
                            storeTitle: order.storeTitle,
                            orderNo: order.orderNo,
                            status: order.status,
                            failedDelivery: order.failedDelivery,
                        },
                        failed,
                    });
                }
                // Not known by its number (or a store could not be asked):
                // the index still matches by tracking number and covers a
                // store the direct call failed on.
                return this.findInIndex(code).pipe(
                    map((result) => ({
                        ...result,
                        failed: _uniq(result.failed.concat(failed)),
                    })),
                );
            }),
        );
    }

    private findInIndex(code: string): Observable<CancelledOrderLookup> {
        return this.loadIndex().pipe(
            map((stores) => ({
                match:
                    stores
                        .flatMap((store) => store.orders)
                        .find((order) => matchesCancelledOrder(order, code)) ??
                    null,
                failed: _compact(stores.map((store) => store.failedTitle)),
            })),
        );
    }

    // ---- Direct lookup by order number ----

    // Asks every store of the platform for the order; the first that knows
    // it wins. A store that errors is reported, not treated as "not there".
    private findDirect(
        platform: OrderNumberPlatform,
        code: string,
    ): Observable<{ order: DirectOrder | null; failed: string[] }> {
        const orderNo = normalizeCode(code);
        const stores = STORES.filter((store) => store.platform === platform);
        return from$(stores).pipe(
            mergeMap((store) =>
                this.fetchDirect(store, orderNo).pipe(
                    map((order) => ({ order, failedTitle: undefined })),
                    catchError((error: unknown) => {
                        console.warn(
                            `${store.storeId}: order lookup failed —`,
                            this.utilService.errorToString(error),
                        );
                        return of({ order: null, failedTitle: store.title });
                    }),
                ),
            ),
            toArray(),
            map((results) => ({
                order: results.find((r) => r.order)?.order ?? null,
                failed: _compact(results.map((r) => r.failedTitle)),
            })),
        );
    }

    private fetchDirect(
        store: StoreConfig,
        orderNo: string,
    ): Observable<DirectOrder | null> {
        switch (store.platform) {
            case 'shopee':
                return this.fetchShopeeOrder(store, orderNo);
            case 'lazada':
                return this.fetchLazadaOrder(store, orderNo);
            case 'tiktok':
                return this.fetchTiktokOrder(store, orderNo);
        }
    }

    private fetchShopeeOrder(
        store: ShopeeStoreConfig,
        orderSn: string,
    ): Observable<DirectOrder | null> {
        const service = storeService(
            this.injector,
            store,
            'ssmAuthInfo',
            ShopeeOpenServiceV2,
        );
        return service.getOrderDetails([orderSn]).pipe(
            reduce(
                (all: any[], chunk: any[]) => all.concat(chunk),
                [] as any[],
            ),
            catchError((error: unknown) =>
                String(_get(error, 'message') ?? '').startsWith(
                    SHOPEE_NOT_FOUND,
                )
                    ? of([] as any[])
                    : throwError(error),
            ),
            mergeMap((orders: any[]) => {
                const order = orders.find((entry) => entry?.order_sn);
                if (!order) {
                    return of(null);
                }
                const status = _toString(order.order_status ?? '');
                const failedDelivery =
                    status === ShopeeOrderStatus.Cacelled &&
                    isShopeeFailedDelivery(order);
                const base: DirectOrder = {
                    storeId: store.storeId,
                    storeTitle: store.title,
                    orderNo: _toString(order.order_sn),
                    status,
                    cancelled:
                        status === ShopeeOrderStatus.Cacelled &&
                        !failedDelivery,
                    failedDelivery,
                    trackingNumbers: [],
                    createdAt: order.create_time
                        ? moment.unix(order.create_time)
                        : null,
                };
                if (!base.cancelled) {
                    return of(base);
                }
                return service.getTrackingNumbers([orderSn]).pipe(
                    map((found: any[]): DirectOrder => ({
                        ...base,
                        trackingNumbers: _compact(
                            found.map((entry) =>
                                _toString(entry.trackingNumber || ''),
                            ),
                        ),
                    })),
                    catchError(() => of(base)),
                );
            }),
        );
    }

    private fetchLazadaOrder(
        store: LazadaStoreConfig,
        orderId: string,
    ): Observable<DirectOrder | null> {
        const service = storeService(
            this.injector,
            store,
            'lazadaAuthInfo',
            LazadaOpenService,
        );
        return service.getOrder(orderId).pipe(
            // Lazada answers an unknown order with a non-zero code (E016
            // Invalid Order ID, verified 2026-09-22), which the delegate
            // wrapper throws; that is "not this store", not a fault.
            catchError(() => of(null)),
            mergeMap((order: any) => {
                if (!order || !order.order_id) {
                    return of(null);
                }
                const status = _toString(_get(order, 'statuses[0]') ?? '');
                const created = moment(order.created_at);
                const base: DirectOrder = {
                    storeId: store.storeId,
                    storeTitle: store.title,
                    orderNo: _toString(order.order_id),
                    status,
                    cancelled: status === LazadaOrderStatus.Canceled,
                    // Lazada gives a failed delivery its own statuses.
                    failedDelivery:
                        status === LazadaOrderStatus.Failed ||
                        status.startsWith(LazadaOrderStatus.ShippedBack),
                    trackingNumbers: [],
                    createdAt: created.isValid() ? created : null,
                };
                if (!base.cancelled) {
                    return of(base);
                }
                return service.getOrderItems([base.orderNo]).pipe(
                    map((itemLists: any[]): DirectOrder => {
                        const items: any[] =
                            _get(itemLists, '[0].order_items') || [];
                        const codes: string[] = items.map((item: any) =>
                            _toString(item.tracking_code || ''),
                        );
                        return {
                            ...base,
                            trackingNumbers: _uniq(_compact(codes)),
                        };
                    }),
                    catchError(() => of(base)),
                );
            }),
        );
    }

    private fetchTiktokOrder(
        store: TiktokStoreConfig,
        orderId: string,
    ): Observable<DirectOrder | null> {
        const service = storeService(
            this.injector,
            store,
            'tiktokAuthInfo',
            TiktokOpenService,
        );
        return service.getOrderDetail([orderId]).pipe(
            map((responses: any[]) => {
                const orders: any[] = responses.flatMap((response: any) =>
                    Array.isArray(response)
                        ? response
                        : _get(response, 'orders') || [],
                );
                // An unknown id comes back as a stub with no `id` (verified
                // 2026-09-22), so this is the "not found" test.
                const order = orders.find((entry) => entry?.id);
                if (!order) {
                    return null;
                }
                const status = _toString(order.status ?? '');
                const failedDelivery =
                    status === TiktokOrderStatus.Cancelled &&
                    isTiktokFailedDelivery(order);
                return {
                    storeId: store.storeId,
                    storeTitle: store.title,
                    orderNo: _toString(order.id),
                    status,
                    cancelled:
                        status === TiktokOrderStatus.Cancelled &&
                        !failedDelivery,
                    failedDelivery,
                    trackingNumbers: _compact([
                        _toString(order.tracking_number || ''),
                    ]),
                    createdAt: order.create_time
                        ? moment.unix(order.create_time)
                        : null,
                };
            }),
        );
    }

    // ---- Index of recent cancelled orders, for tracking-number scans ----

    private loadIndex(): Observable<StoreIndex[]> {
        const now = Date.now();
        if (this.index && now - this.index.at < INDEX_TTL_MS) {
            return this.index.result;
        }
        const since = moment().startOf('day').subtract(LOOKBACK_DAYS, 'days');
        const result = from$(STORES).pipe(
            mergeMap(
                (store) => this.fetchStore(store, since),
                STORE_SYNC_CONCURRENCY,
            ),
            toArray(),
            shareReplay(1),
        );
        this.index = { at: now, result };
        return result;
    }

    private fetchStore(
        store: StoreConfig,
        since: Moment,
    ): Observable<StoreIndex> {
        const orders: Observable<CancelledOrder[]> =
            store.platform === 'lazada'
                ? this.fetchLazada(store, since)
                : store.platform === 'shopee'
                  ? this.fetchShopee(store, since)
                  : this.fetchTiktok(store, since);
        return orders.pipe(
            map((list) => ({ orders: list })),
            catchError((error: unknown) => {
                console.warn(
                    `${store.storeId}: cancelled orders lookup failed —`,
                    this.utilService.errorToString(error),
                );
                return of({ orders: [], failedTitle: store.title });
            }),
        );
    }

    // Lazada's cancelled orders in the window, paged, then their items for
    // the tracking codes.
    private fetchLazada(
        store: LazadaStoreConfig,
        since: Moment,
    ): Observable<CancelledOrder[]> {
        const service = storeService(
            this.injector,
            store,
            'lazadaAuthInfo',
            LazadaOpenService,
        );
        return service
            .getAllOrdersByStatus(LazadaOrderStatus.Canceled, since)
            .pipe(
                mergeMap((orders: any[]) => {
                    const orderIds = orders.map((order) =>
                        _toString(order.order_id),
                    );
                    return service.getOrderItems(orderIds).pipe(
                        map((itemLists: any[]) =>
                            orders.map((order): CancelledOrder => {
                                const entry = itemLists.find(
                                    (list: any) =>
                                        _toString(list.order_id) ===
                                        _toString(order.order_id),
                                );
                                const items: any[] =
                                    _get(entry, 'order_items') || [];
                                const codes: string[] = items.map((item: any) =>
                                    _toString(item.tracking_code || ''),
                                );
                                const created = moment(order.created_at);
                                return {
                                    storeId: store.storeId,
                                    storeTitle: store.title,
                                    orderNo: _toString(order.order_id),
                                    trackingNumbers: _uniq(_compact(codes)),
                                    createdAt: created.isValid()
                                        ? created
                                        : null,
                                };
                            }),
                        ),
                    );
                }),
            );
    }

    // Shopee: the cancelled order numbers per 14-day window, their details
    // (for whether a shipment was ever arranged, and to drop the failed
    // deliveries), then a tracking number per arranged order.
    private fetchShopee(
        store: ShopeeStoreConfig,
        since: Moment,
    ): Observable<CancelledOrder[]> {
        const service = storeService(
            this.injector,
            store,
            'ssmAuthInfo',
            ShopeeOpenServiceV2,
        );
        return from$(
            splitWindows(since, moment(), SHOPEE_LIST_WINDOW_DAYS),
        ).pipe(
            mergeMap(
                (window) =>
                    service.getOrderSnsByStatus(
                        window.from,
                        window.to,
                        ShopeeOrderStatus.Cacelled,
                    ),
                1,
            ),
            reduce(
                (all: string[], page: any[]) =>
                    all.concat(page.map((entry) => _toString(entry.orderSn))),
                [] as string[],
            ),
            mergeMap((orderSns) =>
                orderSns.length === 0
                    ? of([] as any[])
                    : service
                          .getOrderDetails(orderSns)
                          .pipe(
                              reduce(
                                  (all: any[], chunk: any[]) =>
                                      all.concat(chunk),
                                  [] as any[],
                              ),
                          ),
            ),
            mergeMap((details: any[]) =>
                from$(
                    details.filter((detail) => !isShopeeFailedDelivery(detail)),
                ).pipe(
                    mergeMap((detail: any) => {
                        const arranged = (detail.package_list || []).some(
                            (pkg: any) =>
                                pkg.logistics_status &&
                                pkg.logistics_status !==
                                    'LOGISTICS_NOT_STARTED',
                        );
                        const base: CancelledOrder = {
                            storeId: store.storeId,
                            storeTitle: store.title,
                            orderNo: _toString(detail.order_sn),
                            trackingNumbers: [],
                            createdAt: detail.create_time
                                ? moment.unix(detail.create_time)
                                : null,
                        };
                        if (!arranged) {
                            return of(base);
                        }
                        return service.getTrackingNumbers([base.orderNo]).pipe(
                            map((found: any[]): CancelledOrder => ({
                                ...base,
                                trackingNumbers: _compact(
                                    found.map((entry) =>
                                        _toString(entry.trackingNumber || ''),
                                    ),
                                ),
                            })),
                            // No tracking is still a cancelled order,
                            // findable by its number.
                            catchError(() => of(base)),
                        );
                    }, SHOPEE_TRACKING_CONCURRENCY),
                    toArray(),
                ),
            ),
        );
    }

    // TikTok carries the tracking number on the order itself.
    private fetchTiktok(
        store: TiktokStoreConfig,
        since: Moment,
    ): Observable<CancelledOrder[]> {
        const service = storeService(
            this.injector,
            store,
            'tiktokAuthInfo',
            TiktokOpenService,
        );
        return service
            .searchOrders(TiktokOrderStatus.Cancelled, since.unix())
            .pipe(
                map((orders: any[]) =>
                    orders.filter((order) => !isTiktokFailedDelivery(order)),
                ),
                map((orders: any[]) =>
                    orders.map((order): CancelledOrder => ({
                        storeId: store.storeId,
                        storeTitle: store.title,
                        orderNo: _toString(order.id),
                        trackingNumbers: _compact([
                            _toString(order.tracking_number || ''),
                        ]),
                        createdAt: order.create_time
                            ? moment.unix(order.create_time)
                            : null,
                    })),
                ),
            );
    }
}
